<template>
    <div class="table-container w-full">
        <div class="mt-1 flex justify-between">
            <div class="lg:w-1/3 w-full">
                <a-input-search
                    data-cy="associations-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
            <div>
                <ui-date-range-picker-double v-model="expiry_date_range" @changeDates="handleDateFilterChange" />
            </div>
            <div>
                <a-button-group>
                    <a-button data-cy="create-association-button" type="primary" @click="onAdd">
                        {{ $t('admin.association_settings.add') }}
                    </a-button>
                </a-button-group>
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="association-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('admin.association_settings.edit')">
                                    <a-button
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'edit')"
                                    >
                                        <EditOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else-if="column.key === 'is_active'">
                            <span class="font-semibold text-default-red" v-if="!dateCheck(record.expiry_date)">
                                {{ $t('association.table.columns.inactive') }}
                            </span>
                            <span class="font-semibold text-default-green" v-if="dateCheck(record.expiry_date)">
                                {{ $t('association.table.columns.active') }}
                            </span>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import UiDateRangePickerDouble from '@/components/ui/DateRangePickerDouble.vue';
import { EditOutlined, ExclamationCircleFilled, MinusCircleFilled } from '@ant-design/icons-vue';

export default {
    components: {
        UiDateRangePickerDouble,
        EditOutlined,
        ExclamationCircleFilled,
        MinusCircleFilled,
    },
    data() {
        return {
            data: [],
            expiry_date_range: { start_date: null, end_date: null },
            search_value: null,
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
        };
    },
    mounted() {
        this.fetch();
    },
    computed: {
        getColumns() {
            return [
                {
                    key: 'actions',
                    width: 35,
                },
                {
                    title: this.$t('globals.tables.association.headers.name'),
                    key: 'name',
                    dataIndex: 'name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.association.headers.association_code'),
                    key: 'association_code',
                    dataIndex: 'association_code',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.association.headers.association_id'),
                    key: 'association_id',
                    dataIndex: 'association_id',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.association.headers.admin_email'),
                    key: 'admin_email',
                    dataIndex: 'admin_email',
                    sorter: true,
                    ellipsis: true,
                },
                {
                    title: this.$t('globals.tables.association.headers.expiry_date'),
                    key: 'expiry_date',
                    dataIndex: 'expiry_date',
                    sorter: false,
                },
                {
                    title: this.$t('globals.tables.association.headers.active_status'),
                    key: 'is_active',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
    },
    methods: {
        onSelectChange(selected_row_keys, selected_rows) {
            this.selected_row_keys = selected_row_keys;
        },
        fetch(params = {}) {
            this.loading = true;
            this.params = { ...params };
            let start_date = null;
            let end_date = null;

            if (params.expiry_date_range !== undefined) {
                start_date = params.expiry_date_range.start_date
                    ? params.expiry_date_range.start_date.format('YYYY-MM-DD')
                    : null;
                end_date = params.expiry_date_range.end_date
                    ? params.expiry_date_range.end_date.format('YYYY-MM-DD')
                    : null;

                delete params.expiry_date_range;
            }

            this.$http
                .get('/api/associations', {
                    params: {
                        start_date: start_date,
                        end_date: end_date,
                        ...params,
                    },
                })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVASF01' }),
                        });
                    }
                });
        },
        dateCheck(expiry_date) {
            return this.$dayjs(expiry_date).isSameOrAfter(this.$dayjs());
        },
        onTableAction(id, key) {
            this.$emit('onTableAction', {
                id: id,
                key: key,
            });
        },
        onAdd() {
            this.$emit('onAdd');
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.fetch({
                    search_value: this.search_value,
                    expiry_date_range: this.expiry_date_range,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        handleDateFilterChange() {
            this.onSelectChange([]);

            this.fetch({
                search_value: this.search_value,
                expiry_date_range: this.expiry_date_range,
            });

            this.pagination.current = 1;
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
    },
};
</script>
